/**
 * PlatformBackupsPage
 *
 * Unified Operations > Backups page combining:
 * - Tier-based backup policy management (WQ-036/WQ-037)
 * - Platform-level backup trigger, scheduling, restore, and history (WQ-033)
 *
 * @module pages/PlatformBackupsPage
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  DatabaseIcon,
  RefreshIcon,
  AlertIcon,
  CheckCircleIcon,
  ArchiveIcon,
  SettingsIcon,
  ClockIcon,
  CloudIcon,
  ShieldIcon,
  UndoIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '../components/common/Icons';
import type {
  BackupPolicy,
  BackupPolicyUpdate,
} from '../services/backupPolicyService';
import * as policyService from '../services/backupPolicyService';
import {
  triggerPlatformBackup,
  getPlatformBackupHistory,
  getPlatformBackupConfig,
  upsertPlatformBackupConfig,
  togglePlatformBackupSchedule,
  getPlatformNextRun,
  initiatePlatformRestore,
  getPlatformRestoreStatus,
  deletePlatformBackup,
} from '../services/platformBackupService';
import type {
  PlatformBackupHistoryItem,
  PlatformBackupHistoryResponse,
  PlatformBackupConfigResponse,
  PlatformBackupNextRunResponse,
  PlatformRestoreDetailResponse,
} from '../services/platformBackupService';

// =============================================================================
// Helpers
// =============================================================================

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(iso: string | null): string {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleString();
}

/** Convert cron expression or keyword to human-readable label */
function formatFrequency(freq: string): string {
  const CRON_LABELS: Record<string, string> = {
    'hourly': 'Hourly',
    'daily': 'Daily',
    'weekly': 'Weekly',
    '0 */6 * * *': 'Every 6 hours',
    '0 2 * * *': 'Daily (2 AM)',
    '0 3 * * *': 'Daily (3 AM)',
    '0 2 * * 0': 'Weekly (Sun 2 AM)',
    '0 0 * * *': 'Daily (midnight)',
    '0 */12 * * *': 'Every 12 hours',
  };
  return CRON_LABELS[freq] || freq;
}

function getStatusBadge(status: string) {
  const colors: Record<string, { bg: string; border: string; text: string }> = {
    completed: { bg: 'rgba(34, 197, 94, 0.15)', border: 'rgba(34, 197, 94, 0.4)', text: '#22c55e' },
    pending:   { bg: 'rgba(234, 179, 8, 0.15)', border: 'rgba(234, 179, 8, 0.4)', text: '#eab308' },
    in_progress: { bg: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.4)', text: '#3b82f6' },
    failed:    { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.4)', text: '#ef4444' },
    cancelled: { bg: 'rgba(107, 114, 128, 0.15)', border: 'rgba(107, 114, 128, 0.4)', text: '#6b7280' },
    verifying: { bg: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.4)', text: '#8b5cf6' },
    restoring: { bg: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.4)', text: '#3b82f6' },
    verifying_health: { bg: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.4)', text: '#8b5cf6' },
    rolled_back: { bg: 'rgba(249, 115, 22, 0.15)', border: 'rgba(249, 115, 22, 0.4)', text: '#f97316' },
  };
  const c = colors[status] || colors.cancelled;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      border: `1px solid ${c.border}`,
      color: c.text,
    }}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

const tierBadgeColors: Record<string, string> = {
  daily: '#3b82f6',
  weekly: '#8b5cf6',
  monthly: '#f59e0b',
  on_demand: '#6b7280',
};

const tierBadgeLabels: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  on_demand: 'On Demand',
};

function RetentionTierBadge({ tier }: { tier?: string }) {
  if (!tier) return null;
  const color = tierBadgeColors[tier] || '#6b7280';
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: '0.75rem',
      fontWeight: 500,
      background: `${color}22`,
      color: color,
      border: `1px solid ${color}44`,
    }}>
      {tierBadgeLabels[tier] || tier}
    </span>
  );
}

const filterSelectStyle: React.CSSProperties = {
  padding: '4px 8px',
  background: 'rgba(0, 0, 0, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '6px',
  color: 'var(--text-primary)',
  fontSize: '12px',
  cursor: 'pointer',
};

function getDestinationPath(destType: string, destConfig?: Record<string, unknown> | null): string {
  switch (destType) {
    case 's3': {
      const bucket = (destConfig?.bucket as string) || '<bucket>';
      return `s3://${bucket}/backups/_platform/`;
    }
    case 'azure_blob': {
      const account = (destConfig?.account_name as string) || '<account>';
      const container = (destConfig?.container_name as string) || '<container>';
      return `https://${account}.blob.core.windows.net/${container}/backups/_platform/`;
    }
    case 'gcs': {
      const bucket = (destConfig?.bucket as string) || '<bucket>';
      return `gs://${bucket}/backups/_platform/`;
    }
    default:
      return '/backups/_platform/';
  }
}

const FREQUENCY_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: '0 2 * * *', label: 'Daily at 2:00 AM' },
  { value: '0 2 * * 0', label: 'Weekly (Sunday 2:00 AM)' },
];

const cardStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '12px',
  padding: '16px 20px',
  marginBottom: '24px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: '4px',
  display: 'block',
};

const inputStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '6px',
  padding: '6px 10px',
  color: 'var(--text-primary)',
  fontSize: '13px',
  width: '100%',
};

// =============================================================================
// Backup Policy Constants & Card
// =============================================================================

const TIER_DISPLAY: Record<string, { label: string; color: string; description: string }> = {
  starter: {
    label: 'Starter',
    color: '#22c55e',
    description: 'Row-level tenants — weekly backups with basic retention',
  },
  professional: {
    label: 'Professional',
    color: '#3b82f6',
    description: 'Schema-level tenants — daily backups with extended retention',
  },
  enterprise: {
    label: 'Enterprise',
    color: '#a855f7',
    description: 'Container-level tenants — frequent backups with full retention',
  },
  platform: {
    label: 'Platform',
    color: '#f59e0b',
    description: 'Platform-wide backup covering management database and configs',
  },
};

const DEST_LABELS: Record<string, string> = {
  local: 'Local Filesystem',
  s3: 'Amazon S3',
  azure_blob: 'Azure Blob Storage',
  gcs: 'Google Cloud Storage',
  external: 'External Mount',
};

const DEST_FIELDS: Record<string, Array<{ key: string; label: string; placeholder: string }>> = {
  local: [
    { key: 'base_path', label: 'Backup Path', placeholder: '/backups' },
  ],
  s3: [
    { key: 'bucket', label: 'S3 Bucket', placeholder: 'my-backup-bucket' },
    { key: 'prefix', label: 'Key Prefix', placeholder: 'backups/' },
    { key: 'region', label: 'AWS Region', placeholder: 'us-east-1' },
  ],
  azure_blob: [
    { key: 'container_name', label: 'Container', placeholder: 'backups' },
    { key: 'prefix', label: 'Blob Prefix', placeholder: 'tenant-data/' },
  ],
  gcs: [
    { key: 'bucket', label: 'GCS Bucket', placeholder: 'my-backup-bucket' },
    { key: 'prefix', label: 'Object Prefix', placeholder: 'backups/' },
  ],
  external: [
    { key: 'mount_path', label: 'Mount Path', placeholder: '/mnt/backups' },
  ],
};

const policyCardStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '12px',
  padding: '20px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
};

const policyInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  background: 'rgba(0, 0, 0, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '8px',
  color: 'var(--text-primary)',
  fontSize: '13px',
  boxSizing: 'border-box' as const,
};

const policySelectStyle: React.CSSProperties = { ...policyInputStyle, cursor: 'pointer' };
const policyLabelStyle: React.CSSProperties = {
  display: 'block', fontSize: '12px', color: 'var(--text-secondary)',
  marginBottom: '4px', fontWeight: 500,
};

interface PolicyCardProps {
  policy: BackupPolicy;
  onSaved: (p: BackupPolicy) => void;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const PolicyCard: React.FC<PolicyCardProps> = ({ policy, onSaved, authFetch }) => {
  const info = TIER_DISPLAY[policy.tier] || { label: policy.tier, color: '#888', description: '' };
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [frequency, setFrequency] = useState(policy.frequency);
  const [retentionCount, setRetentionCount] = useState(policy.retention_count);
  const [retentionDays, setRetentionDays] = useState(policy.retention_days);
  const [weeklyRetention, setWeeklyRetention] = useState(policy.weekly_retention_count ?? 4);
  const [monthlyRetention, setMonthlyRetention] = useState(policy.monthly_retention_count ?? 3);
  const [destType, setDestType] = useState(policy.destination_type);
  const [destConfig, setDestConfig] = useState<Record<string, string>>(policy.destination_config || {});
  const [enabled, setEnabled] = useState(policy.enabled);

  useEffect(() => {
    setFrequency(policy.frequency);
    setRetentionCount(policy.retention_count);
    setRetentionDays(policy.retention_days);
    setWeeklyRetention(policy.weekly_retention_count ?? 4);
    setMonthlyRetention(policy.monthly_retention_count ?? 3);
    setDestType(policy.destination_type);
    setDestConfig(policy.destination_config || {});
    setEnabled(policy.enabled);
  }, [policy]);

  const handleCancel = () => {
    setFrequency(policy.frequency);
    setRetentionCount(policy.retention_count);
    setRetentionDays(policy.retention_days);
    setWeeklyRetention(policy.weekly_retention_count ?? 4);
    setMonthlyRetention(policy.monthly_retention_count ?? 3);
    setDestType(policy.destination_type);
    setDestConfig(policy.destination_config || {});
    setEnabled(policy.enabled);
    setTestResult(null);
    setErrorMsg(null);
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg(null);
    try {
      const update: BackupPolicyUpdate = {
        frequency,
        retention_count: retentionCount,
        retention_days: retentionDays,
        weekly_retention_count: weeklyRetention,
        monthly_retention_count: monthlyRetention,
        destination_type: destType,
        destination_config: destConfig,
        enabled,
      };
      const saved = await policyService.updatePolicy(authFetch, policy.tier, update);
      onSaved(saved);
      setEditing(false);
      setSuccessMsg('Policy saved');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await policyService.testConnection(authFetch, destType, destConfig);
      setTestResult(result);
    } catch (err: unknown) {
      setTestResult({ success: false, message: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleToggle = async () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);
    try {
      const saved = await policyService.updatePolicy(authFetch, policy.tier, { enabled: newEnabled });
      onSaved(saved);
    } catch (err: unknown) {
      setEnabled(!newEnabled);
      setErrorMsg(err instanceof Error ? err.message : 'Toggle failed');
    }
  };

  return (
    <div style={{
      ...policyCardStyle,
      borderLeft: `4px solid ${info.color}`,
      opacity: enabled ? 1 : 0.6,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', color: info.color }}>{info.label}</h3>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>{info.description}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            className={`btn btn-sm ${enabled ? 'btn-success' : 'btn-secondary'}`}
            onClick={handleToggle}
            style={{
              minWidth: '70px',
              ...(enabled ? {
                background: 'rgba(34, 197, 94, 0.2)',
                borderColor: 'rgba(34, 197, 94, 0.5)',
                color: 'var(--success)',
              } : {}),
            }}
          >
            {enabled ? 'Active' : 'Off'}
          </button>
          {!editing && (
            <button className="btn btn-sm btn-secondary" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Alerts */}
      {successMsg && (
        <div className="alert alert-success" style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <CheckCircleIcon size={14} /> {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="alert alert-error" style={{ marginBottom: '12px' }}>{errorMsg}</div>
      )}

      {/* Read-only summary */}
      {!editing && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '13px' }}>
          <div>
            <span style={policyLabelStyle}><ClockIcon size={12} /> Frequency</span>
            <div style={{ color: 'var(--text-primary)' }}>{formatFrequency(frequency)}</div>
          </div>
          <div>
            <span style={policyLabelStyle}><DatabaseIcon size={12} /> Retention</span>
            <div style={{ color: 'var(--text-primary)' }}>
              Daily: {retentionCount} &middot; Weekly: {policy.weekly_retention_count ?? '\u2014'} &middot; Monthly: {policy.monthly_retention_count ?? '\u2014'} &middot; Expires: {retentionDays}d
            </div>
          </div>
          <div>
            <span style={policyLabelStyle}><CloudIcon size={12} /> Destination</span>
            <div style={{ color: 'var(--text-primary)' }}>{DEST_LABELS[destType] || destType}</div>
          </div>
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <span style={policyLabelStyle}>Frequency</span>
              <select style={policySelectStyle} value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                <option value="hourly">Hourly</option>
                <option value="0 */6 * * *">Every 6 hours</option>
                <option value="0 2 * * *">Daily (2am)</option>
                <option value="daily">Daily</option>
                <option value="0 2 * * 0">Weekly (Sun 2am)</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div>
              <span style={policyLabelStyle}>Daily (count)</span>
              <input type="number" style={policyInputStyle} value={retentionCount} min={1} max={365}
                onChange={(e) => setRetentionCount(parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <span style={policyLabelStyle}>Weekly (count)</span>
              <input type="number" style={policyInputStyle} value={weeklyRetention} min={1} max={365}
                onChange={(e) => setWeeklyRetention(parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <span style={policyLabelStyle}>Monthly (count)</span>
              <input type="number" style={policyInputStyle} value={monthlyRetention} min={1} max={365}
                onChange={(e) => setMonthlyRetention(parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <span style={policyLabelStyle}>Expires (days)</span>
              <input type="number" style={policyInputStyle} value={retentionDays} min={1} max={3650}
                onChange={(e) => setRetentionDays(parseInt(e.target.value) || 1)} />
            </div>
          </div>

          {/* Destination */}
          <div style={{ marginBottom: '12px' }}>
            <span style={policyLabelStyle}>Destination Type</span>
            <select style={policySelectStyle} value={destType} onChange={(e) => { setDestType(e.target.value); setDestConfig({}); setTestResult(null); }}>
              {Object.entries(DEST_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            {(DEST_FIELDS[destType] || []).map((field) => (
              <div key={field.key}>
                <span style={policyLabelStyle}>{field.label}</span>
                <input type="text" style={policyInputStyle} value={destConfig[field.key] || ''} placeholder={field.placeholder}
                  onChange={(e) => setDestConfig((prev) => ({ ...prev, [field.key]: e.target.value }))} />
              </div>
            ))}
          </div>

          {/* Cloud credential note */}
          {destType !== 'local' && destType !== 'external' && (
            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: '0 0 12px', fontStyle: 'italic' }}>
              Credentials are read from environment variables (AWS_ACCESS_KEY_ID, AZURE_STORAGE_CONNECTION_STRING, GOOGLE_APPLICATION_CREDENTIALS). Do not enter secrets here.
            </p>
          )}

          {/* Test Connection */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
            <button className="btn btn-sm btn-secondary" onClick={handleTestConnection} disabled={testing}>
              {testing ? <><RefreshIcon size={14} className="spin" /> Testing...</> : 'Test Connection'}
            </button>
            {testResult && (
              <span style={{ fontSize: '12px', color: testResult.success ? 'var(--success)' : 'var(--danger)' }}>
                {testResult.success ? <CheckCircleIcon size={14} /> : null} {testResult.message}
              </span>
            )}
          </div>

          {/* Save / Cancel */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button className="btn btn-sm btn-secondary" onClick={handleCancel}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><RefreshIcon size={14} className="spin" /> Saving...</> : 'Save Policy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

const PlatformBackupsPage: React.FC = () => {
  const { authFetch } = useAuth();

  // History state
  const [history, setHistory] = useState<PlatformBackupHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Config state
  const [config, setConfig] = useState<PlatformBackupConfigResponse | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSuccess, setConfigSuccess] = useState<string | null>(null);
  const [showConfigPanel, setShowConfigPanel] = useState(false);

  // Config form state
  const [formEnabled, setFormEnabled] = useState(false);
  const [formFrequency, setFormFrequency] = useState('daily');
  const [formRetentionCount, setFormRetentionCount] = useState(7);
  const [formRetentionDays, setFormRetentionDays] = useState(30);
  const [formDestType, setFormDestType] = useState('local');

  // Next run state
  const [nextRun, setNextRun] = useState<PlatformBackupNextRunResponse | null>(null);

  // Restore state
  const [restoreTarget, setRestoreTarget] = useState<PlatformBackupHistoryItem | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreStatus, setRestoreStatus] = useState<PlatformRestoreDetailResponse | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const restorePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<PlatformBackupHistoryItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // History filter state
  const [typeFilter, setTypeFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  // Policy state
  const [policies, setPolicies] = useState<BackupPolicy[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [policiesError, setPoliciesError] = useState<string | null>(null);
  const [showPolicySection, setShowPolicySection] = useState(false);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { backup_type?: string; retention_tier?: string } = {};
      if (typeFilter) filters.backup_type = typeFilter;
      if (tierFilter) filters.retention_tier = tierFilter;
      const data = await getPlatformBackupHistory(authFetch, pageSize, page * pageSize, filters);
      setHistory(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch backup history');
    } finally {
      setLoading(false);
    }
  }, [authFetch, page, typeFilter, tierFilter]);

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const data = await getPlatformBackupConfig(authFetch);
      setConfig(data);
      if (data) {
        setFormEnabled(data.enabled);
        setFormFrequency(data.frequency);
        setFormRetentionCount(data.retention_count);
        setFormRetentionDays(data.retention_days);
        setFormDestType(data.destination_type);
      }
    } catch {
      // Config may not exist yet, that's OK
    } finally {
      setConfigLoading(false);
    }
  }, [authFetch]);

  const fetchNextRun = useCallback(async () => {
    try {
      const data = await getPlatformNextRun(authFetch);
      setNextRun(data);
    } catch {
      // Non-critical
    }
  }, [authFetch]);

  const fetchPolicies = useCallback(async () => {
    if (!authFetch) return;
    setPoliciesLoading(true);
    setPoliciesError(null);
    try {
      const data = await policyService.listPolicies(authFetch);
      setPolicies(data);
    } catch (err: unknown) {
      setPoliciesError(err instanceof Error ? err.message : 'Failed to load policies');
    } finally {
      setPoliciesLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchHistory();
    fetchConfig();
    fetchNextRun();
    fetchPolicies();
  }, [fetchHistory, fetchConfig, fetchNextRun, fetchPolicies]);

  // Clean up restore polling on unmount
  useEffect(() => {
    return () => {
      if (restorePollRef.current) clearInterval(restorePollRef.current);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleTriggerBackup = async () => {
    setTriggering(true);
    setTriggerMessage(null);
    try {
      const result = await triggerPlatformBackup(authFetch);
      setTriggerMessage(result.message);
      setTimeout(() => fetchHistory(), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger backup');
    } finally {
      setTriggering(false);
    }
  };

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    setConfigError(null);
    setConfigSuccess(null);
    try {
      const data = await upsertPlatformBackupConfig(authFetch, {
        enabled: formEnabled,
        frequency: formFrequency,
        retention_count: formRetentionCount,
        retention_days: formRetentionDays,
        destination_type: formDestType,
      });
      setConfig(data);
      setConfigSuccess('Platform backup configuration saved.');
      fetchNextRun();
      setTimeout(() => setConfigSuccess(null), 3000);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setConfigSaving(false);
    }
  };

  const handleToggleSchedule = async () => {
    try {
      const result = await togglePlatformBackupSchedule(authFetch, !formEnabled);
      setFormEnabled(result.enabled);
      if (config) {
        setConfig({ ...config, enabled: result.enabled });
      }
      setConfigSuccess(result.message);
      fetchNextRun();
      setTimeout(() => setConfigSuccess(null), 3000);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to toggle schedule');
    }
  };

  const handleInitiateRestore = async (backup: PlatformBackupHistoryItem) => {
    setRestoreTarget(backup);
    setRestoreStatus(null);
    setRestoreError(null);
  };

  const handleConfirmRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    setRestoreError(null);

    try {
      const result = await initiatePlatformRestore(authFetch, restoreTarget.id);
      // Start polling
      const pollFn = async () => {
        try {
          const status = await getPlatformRestoreStatus(authFetch, result.restore_id);
          setRestoreStatus(status);
          if (['completed', 'failed', 'rolled_back'].includes(status.status)) {
            if (restorePollRef.current) clearInterval(restorePollRef.current);
            setRestoring(false);
            fetchHistory();
          }
        } catch {
          // Keep polling on transient errors
        }
      };
      restorePollRef.current = setInterval(pollFn, 2000);
      // Also poll immediately
      await pollFn();
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Failed to initiate restore');
      setRestoring(false);
    }
  };

  const handleCloseRestore = () => {
    if (restorePollRef.current) clearInterval(restorePollRef.current);
    setRestoreTarget(null);
    setRestoreStatus(null);
    setRestoreError(null);
    setRestoring(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePlatformBackup(authFetch, deleteTarget.id);
      setDeleteTarget(null);
      fetchHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete backup');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handlePolicySaved = (saved: BackupPolicy) => {
    setPolicies((prev) => prev.map((p) => (p.tier === saved.tier ? saved : p)));
  };

  const tenantPolicies = policies.filter((p) => p.tier !== 'platform');
  const platformPolicy = policies.find((p) => p.tier === 'platform');

  const totalPages = history ? Math.ceil(history.total / pageSize) : 0;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1><DatabaseIcon size={24} /> Backups</h1>
          <p className="page-description">
            Platform-level backup management. Trigger, restore, and view history for the platform database and configuration. Tenant backups are managed per-tenant in Tenant Management.
          </p>
        </div>
        <div className="page-header-actions" style={{ gap: '12px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowConfigPanel(p => !p)}
          >
            <SettingsIcon size={16} /> {showConfigPanel ? 'Hide Config' : 'Config'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleTriggerBackup}
            disabled={triggering}
          >
            {triggering ? (
              <><RefreshIcon size={16} className="spin" /> Backing up platform...</>
            ) : (
              <><ArchiveIcon size={16} /> Backup Platform Now</>
            )}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => { fetchHistory(); fetchConfig(); fetchNextRun(); fetchPolicies(); }}
            disabled={loading}
          >
            <RefreshIcon size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* Trigger success message */}
      {triggerMessage && (
        <div className="alert alert-success" style={{ marginBottom: '16px' }}>
          <CheckCircleIcon size={18} />
          <span>{triggerMessage}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setTriggerMessage(null)}
            style={{ marginLeft: 'auto' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
          <AlertIcon size={18} />
          <span>{error}</span>
          <button className="btn btn-sm" onClick={fetchHistory}>Retry</button>
        </div>
      )}

      {/* Backup Policy Section (collapsible) */}
      <div style={cardStyle}>
        <button
          onClick={() => setShowPolicySection(p => !p)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: 0,
            color: 'var(--text-primary)',
          }}
        >
          {showPolicySection ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
          <ShieldIcon size={18} />
          <span style={{ fontSize: '16px', fontWeight: 600 }}>Backup Policy</span>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '8px' }}>
            Tier-based backup policies (ADR-011). Tenants inherit from their tier unless overridden.
          </span>
        </button>

        {showPolicySection && (
          <div style={{ marginTop: '16px' }}>
            {policiesError && (
              <div className="alert alert-error" style={{ marginBottom: '12px' }}>{policiesError}</div>
            )}

            {policiesLoading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
                <RefreshIcon size={16} className="spin" /> Loading policies...
              </div>
            ) : authFetch ? (
              <>
                {/* Tenant Tier Policies */}
                <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <SettingsIcon size={14} /> Tenant Tier Policies
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                  {tenantPolicies.map((p) => (
                    <PolicyCard key={p.tier} policy={p} onSaved={handlePolicySaved} authFetch={authFetch} />
                  ))}
                </div>

                {/* Platform Policy */}
                {platformPolicy && (
                  <>
                    <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <DatabaseIcon size={14} /> Platform Backup Policy
                    </h3>
                    <PolicyCard policy={platformPolicy} onSaved={handlePolicySaved} authFetch={authFetch} />
                  </>
                )}

                {/* Info box */}
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  background: 'rgba(124, 58, 237, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(124, 58, 237, 0.2)',
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                }}>
                  <strong style={{ color: 'var(--text-primary)' }}>How policies work:</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: '18px', lineHeight: 1.7 }}>
                    <li>Each tenant inherits from its tier policy based on isolation level (row=Starter, schema=Professional, container=Enterprise)</li>
                    <li>Override individual tenants from the Tenant Details &rarr; Backups tab</li>
                    <li>Cloud credentials (AWS, Azure, GCS) are read from server environment variables, not stored in policy config</li>
                    <li>Disabling a policy stops scheduled backups for all tenants in that tier (unless overridden)</li>
                  </ul>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Config Panel */}
      {showConfigPanel && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '15px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SettingsIcon size={16} /> Backup Configuration
          </h3>

          {configError && (
            <div className="alert alert-error" style={{ marginBottom: '12px', padding: '8px 12px' }}>
              <AlertIcon size={14} /> <span style={{ fontSize: '12px' }}>{configError}</span>
            </div>
          )}
          {configSuccess && (
            <div className="alert alert-success" style={{ marginBottom: '12px', padding: '8px 12px' }}>
              <CheckCircleIcon size={14} /> <span style={{ fontSize: '12px' }}>{configSuccess}</span>
            </div>
          )}

          {configLoading ? (
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Loading configuration...</p>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                {/* Enable/disable toggle */}
                <div>
                  <label style={labelStyle}>Scheduling</label>
                  <button
                    className={`btn btn-sm ${formEnabled ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={handleToggleSchedule}
                    style={{ width: '100%' }}
                  >
                    {formEnabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>

                {/* Frequency */}
                <div>
                  <label style={labelStyle}>Frequency</label>
                  <select
                    value={formFrequency}
                    onChange={e => setFormFrequency(e.target.value)}
                    style={inputStyle as React.CSSProperties}
                  >
                    {FREQUENCY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Retention Count */}
                <div>
                  <label style={labelStyle}>Keep (max backups)</label>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={formRetentionCount}
                    onChange={e => setFormRetentionCount(Number(e.target.value))}
                    style={inputStyle}
                  />
                </div>

                {/* Retention Days */}
                <div>
                  <label style={labelStyle}>Keep (max days)</label>
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    value={formRetentionDays}
                    onChange={e => setFormRetentionDays(Number(e.target.value))}
                    style={inputStyle}
                  />
                </div>

                {/* Destination */}
                <div>
                  <label style={labelStyle}>Destination</label>
                  <select
                    value={formDestType}
                    onChange={e => setFormDestType(e.target.value)}
                    style={inputStyle as React.CSSProperties}
                  >
                    <option value="local">Local</option>
                    <option value="s3">S3</option>
                    <option value="azure_blob">Azure Blob</option>
                    <option value="gcs">GCS</option>
                  </select>
                </div>
              </div>

              {/* Destination path display */}
              <div style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: '6px',
                padding: '8px 12px',
                marginBottom: '16px',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
              }}>
                <strong>Destination path:</strong>{' '}
                {getDestinationPath(formDestType, config?.destination_config)}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveConfig}
                  disabled={configSaving}
                >
                  {configSaving ? 'Saving...' : 'Save Configuration'}
                </button>

                {nextRun && (
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <ClockIcon size={12} />
                    {nextRun.enabled && nextRun.next_run_at
                      ? `Next run: ${formatDate(nextRun.next_run_at)}`
                      : nextRun.message}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Scope info card */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '14px', marginBottom: '8px', color: 'var(--text-secondary)' }}>
          Backup Scope
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
          Covers: <strong style={{ color: 'var(--text-primary)' }}>platform_core</strong> schema,
          backup_configurations, backup_history, backup_usage_metrics, feature_flags, alembic_version
        </p>
      </div>

      {/* Restore Dialog */}
      {restoreTarget && (
        <div style={{
          ...cardStyle,
          borderColor: 'rgba(249, 115, 22, 0.4)',
          background: 'rgba(249, 115, 22, 0.05)',
        }}>
          <h3 style={{ fontSize: '15px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UndoIcon size={16} /> Restore Platform from Backup
          </h3>

          {!restoreStatus && !restoring && (
            <>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                Restore from backup taken on <strong style={{ color: 'var(--text-primary)' }}>{formatDate(restoreTarget.started_at)}</strong>
                {restoreTarget.size_bytes != null && <> ({formatBytes(restoreTarget.size_bytes)})</>}
              </p>
              <p style={{ fontSize: '12px', color: '#f97316', marginBottom: '12px' }}>
                A pre-restore safety snapshot will be created automatically. If the health check fails after restore, the system will roll back.
              </p>

              {restoreError && (
                <div className="alert alert-error" style={{ marginBottom: '12px', padding: '8px 12px' }}>
                  <AlertIcon size={14} /> <span style={{ fontSize: '12px' }}>{restoreError}</span>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-primary btn-sm" onClick={handleConfirmRestore}>
                  <UndoIcon size={14} /> Confirm Restore
                </button>
                <button className="btn btn-secondary btn-sm" onClick={handleCloseRestore}>
                  Cancel
                </button>
              </div>
            </>
          )}

          {(restoring || restoreStatus) && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                {restoring && <RefreshIcon size={14} className="spin" />}
                <span style={{ fontSize: '13px' }}>
                  Status: {getStatusBadge(restoreStatus?.status || 'pending')}
                </span>
              </div>

              {restoreStatus?.error_message && (
                <p style={{ fontSize: '12px', color: '#ef4444', marginBottom: '8px' }}>
                  {restoreStatus.error_message}
                </p>
              )}

              {restoreStatus && ['completed', 'failed', 'rolled_back'].includes(restoreStatus.status) && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  {restoreStatus.status === 'completed' && (
                    <span style={{ fontSize: '12px', color: '#22c55e' }}>
                      <CheckCircleIcon size={14} /> Platform restored successfully.
                    </span>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={handleCloseRestore}>
                    Close
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <div
          className="modal-overlay"
          onClick={() => !deleting && setDeleteTarget(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            className="glass-panel"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480, padding: '1.5rem', borderRadius: '12px' }}
          >
            <h3 style={{ margin: '0 0 0.75rem' }}>Delete Platform Backup?</h3>
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 0.75rem', fontSize: '13px' }}>
              This will permanently delete the backup record and its storage artifact.
              This action cannot be undone.
            </p>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 1rem' }}>
              Backup: {deleteTarget.id.slice(0, 8)}&hellip;
              {deleteTarget.completed_at && ` \u2014 ${formatDate(deleteTarget.completed_at)}`}
              {deleteTarget.size_bytes != null && ` \u2014 ${formatBytes(deleteTarget.size_bytes)}`}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Backup'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && !history && (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading backup history...</p>
        </div>
      )}

      {/* History Table */}
      {history && (
        <section>
          <h2 style={{ marginBottom: '16px' }}>
            Platform Backup History
            {history.total > 0 && (
              <span style={{ fontSize: '14px', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '8px' }}>
                ({history.total} total)
              </span>
            )}
          </h2>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <select style={filterSelectStyle} value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
              <option value="">All Types</option>
              <option value="scheduled">Scheduled</option>
              <option value="manual">Manual</option>
              <option value="pre_restore">Pre-Restore</option>
            </select>
            <select style={filterSelectStyle} value={tierFilter} onChange={(e) => { setTierFilter(e.target.value); setPage(0); }}>
              <option value="">All Tiers</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="on_demand">On Demand</option>
            </select>
          </div>

          {history.items.length === 0 ? (
            <div className="empty-state">
              <DatabaseIcon size={48} />
              <p>No platform backups yet</p>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Click "Backup Platform Now" to create your first platform backup.
              </p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Type</th>
                      <th>Tier</th>
                      <th>Destination</th>
                      <th>Size</th>
                      <th>Started</th>
                      <th>Completed</th>
                      <th>Error</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.items.map((item: PlatformBackupHistoryItem) => (
                      <tr key={item.id}>
                        <td>{getStatusBadge(item.status)}</td>
                        <td style={{ textTransform: 'capitalize' }}>
                          {item.backup_type.replace(/_/g, ' ')}
                        </td>
                        <td><RetentionTierBadge tier={item.retention_tier} /></td>
                        <td>{item.destination_type}</td>
                        <td>{item.size_bytes != null ? formatBytes(item.size_bytes) : '\u2014'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(item.started_at)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(item.completed_at)}</td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.error_message ? (
                            <span title={item.error_message} style={{ color: '#ef4444', fontSize: '12px' }}>
                              {item.error_message}
                            </span>
                          ) : '\u2014'}
                        </td>
                        <td style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          {item.status === 'completed' && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleInitiateRestore(item)}
                              disabled={restoring}
                              title="Restore from this backup"
                            >
                              <UndoIcon size={12} /> Restore
                            </button>
                          )}
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setDeleteTarget(item)}
                            title="Delete backup"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <TrashIcon size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '16px',
                }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default PlatformBackupsPage;
